import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { archiveExchange, deadLetterExchange, listExchanges, listRecoveryQueue, requestExchangeReplay, requestExchangeRetry } from '../api/exchanges';
import { ApiClientError } from '../api/http';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { PollingIndicator } from '../components/PollingIndicator';
import { RelatedLinks } from '../components/RelatedLinks';
import { useAsyncData } from '../hooks/useAsyncData';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { usePermission } from '../hooks/usePermission';
import { documentDetailLink, documentsByCorrelationLink, documentsByTraceLink, executionsByCorrelationLink } from '../lib/crossLinks';
import { canArchiveExchange, canDeadLetterExchange, canReplayExchange, canRetryExchange, groupExchangesByPartner } from '../lib/exchangeActions';
import type { ExchangeKind, ExchangeListQuery, ExchangeStatus, ExchangeSummary } from '../types/api';

const exchangeKinds: ExchangeKind[] = [
  'DOCUMENT_GENERATE',
  'DOCUMENT_VALIDATE',
  'DOCUMENT_RESEND',
  'DOCUMENT_REBUILD',
];

const statusFocusOptions: Array<{ label: string; value: ExchangeStatus | '' }> = [
  { label: 'All exchanges', value: '' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Waiting retry', value: 'WAITING_RETRY' },
  { label: 'Dead-lettered', value: 'DEAD_LETTERED' },
  { label: 'Archived', value: 'ARCHIVED' },
];

type ExchangeGroupBy = 'none' | 'partner';

const EXCHANGES_POLL_INTERVAL_MS = 20_000;

// Pre-applies filters carried in the incoming URL (e.g. a cross-link from a document,
// execution, or AS2 message detail page — see src/lib/crossLinks.ts), read once on first
// render, mirroring the pattern already used by the AS2 message list pages.
function initialFiltersFromSearch(search: string): ExchangeListQuery {
  const params = new URLSearchParams(search);
  return {
    page: 1,
    pageSize: 20,
    status: '',
    exchangeKind: '',
    documentType: '',
    correlationId: params.get('correlationId') ?? '',
    traceId: params.get('traceId') ?? '',
    referenceId: params.get('referenceId') ?? '',
  };
}

function formatControlError(caught: unknown): string {
  if (caught instanceof ApiClientError && caught.status === 403) {
    return 'Not authorized to perform this runtime action. Ask an administrator for the required exchange operation permission.';
  }
  return caught instanceof Error ? caught.message : 'Unexpected runtime control failure.';
}

export function ExchangesPage({ recoveryMode = false }: { recoveryMode?: boolean }) {
  const { search } = useLocation();
  const { density, setDensity, visibleColumns, setVisibleColumns } = useColumnVisibility({
    technicalIds: !recoveryMode,
    document: true,
    correlation: true,
    updated: true,
  });
  const [filters, setFilters] = useState<ExchangeListQuery>(() => initialFiltersFromSearch(search));
  const [groupBy, setGroupBy] = useState<ExchangeGroupBy>('none');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pollingPaused, setPollingPaused] = useState(false);
  const canRequestRetry = usePermission('integration.execution.retry');
  const canRequestReplay = usePermission('integration.execution.replay');
  const canDeadLetter = usePermission('integration.dlq.discard');
  // Archive shares the dead-letter permission -- see the matching comment on the backend route
  // (apps/integration/backend/src/interfaces/http/routes/exchanges.route.ts).
  const canArchive = usePermission('integration.dlq.discard');

  const { data, isLoading, error, refresh } = useAsyncData(
    async () => {
      if (!recoveryMode || filters.status) return listExchanges(filters);
      return listRecoveryQueue(filters);
    },
    [JSON.stringify(filters)],
    {
      debounceMs: 250,
      pollIntervalMs: pollingPaused ? undefined : EXCHANGES_POLL_INTERVAL_MS,
      isPollingSuspended: Boolean(confirm),
    },
  );

  const exchanges = data?.data ?? [];
  const failedCount = exchanges.filter((item) => item.status === 'FAILED').length;
  const waitingRetryCount = exchanges.filter((item) => item.status === 'WAITING_RETRY').length;
  const deadLetterCount = exchanges.filter((item) => item.status === 'DEAD_LETTERED').length;
  const groups = groupBy === 'partner' ? groupExchangesByPartner(exchanges) : [];
  const selectedExchanges = exchanges.filter((exchange) => selectedIds.has(exchange.id));
  const eligibleRetryIds = selectedExchanges.filter(canRetryExchange).map((exchange) => exchange.id);
  const eligibleDeadLetterIds = selectedExchanges.filter(canDeadLetterExchange).map((exchange) => exchange.id);
  const eligibleReplayIds = selectedExchanges.filter(canReplayExchange).map((exchange) => exchange.id);
  const eligibleArchiveIds = selectedExchanges.filter(canArchiveExchange).map((exchange) => exchange.id);

  function applyFilters(next: ExchangeListQuery) {
    setFilters(next);
    setSelectedIds(new Set());
  }

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((current) => {
      const allSelected = ids.length > 0 && ids.every((id) => current.has(id));
      const next = new Set(current);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function reportBulkOutcome(action: string, total: number, results: PromiseSettledResult<ExchangeSummary | unknown>[]) {
    const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failures.length === 0) return;
    setActionError(
      `${failures.length} of ${total} exchanges failed to ${action}${failures.length === total ? '' : ` (${total - failures.length} succeeded)`}: ${formatControlError(failures[0].reason)}`,
    );
  }

  // Bulk actions run as a batch of independent per-exchange requests (there's no bulk runtime
  // endpoint). Unlike a single-exchange action, onConfirm never re-throws here — with N
  // independent outcomes there's no single pass/fail to hand back to the confirm dialog, so
  // it always closes and any partial-failure summary is surfaced via the page-level banner.
  async function runBulkRetry(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(ids.map((id) => requestExchangeRetry(id, reason.trim() || undefined)));
    reportBulkOutcome('retry', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  async function runBulkDeadLetter(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(
      ids.map((id) => deadLetterExchange(id, reason.trim() || 'Bulk operator DLQ review', { quarantineMode: true, operatorComment: reason.trim() })),
    );
    reportBulkOutcome('dead-letter', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  async function runBulkReplay(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(ids.map((id) => requestExchangeReplay(id, reason.trim() || undefined)));
    reportBulkOutcome('replay', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  async function runBulkArchive(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(ids.map((id) => archiveExchange(id, reason.trim() || 'Bulk operator archive')));
    reportBulkOutcome('archive', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  function buildBulkRetryConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    return {
      title: 'Bulk retry exchanges',
      message: `Request a runtime retry for ${count} selected exchange${count === 1 ? '' : 's'}. Each is retried independently.`,
      confirmLabel: `Retry ${count} exchange${count === 1 ? '' : 's'}`,
      risk: 'medium',
      detailLabel: 'Operator reason',
      detailValue: reason,
      detailPlaceholder: 'Why retry is appropriate now',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkRetryConfirmState(ids, value)),
      onConfirm: () => runBulkRetry(ids, reason),
    };
  }

  function buildBulkDeadLetterConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    // Friction scales with blast radius, matching the AS2 DLQ bulk-discard pattern: type the
    // exact count being dead-lettered rather than a fixed word.
    const requireText = `DLQ ${count}`;
    return {
      title: 'Bulk dead-letter exchanges',
      message: `Quarantine ${count} selected exchange${count === 1 ? '' : 's'} for operator review. Type "${requireText}" to confirm.`,
      confirmLabel: `Dead-letter ${count} exchange${count === 1 ? '' : 's'}`,
      risk: 'high',
      requireText,
      detailLabel: 'Operator annotation',
      detailValue: reason,
      detailPlaceholder: 'Why these exchanges are being dead-lettered',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkDeadLetterConfirmState(ids, value)),
      onConfirm: () => runBulkDeadLetter(ids, reason),
    };
  }

  function buildBulkReplayConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    const requireText = `REPLAY ${count}`;
    return {
      title: 'Bulk replay exchanges',
      message: `Request a replay for ${count} selected exchange${count === 1 ? '' : 's'} after correcting the underlying configuration. Type "${requireText}" to confirm.`,
      confirmLabel: `Replay ${count} exchange${count === 1 ? '' : 's'}`,
      risk: 'high',
      requireText,
      detailLabel: 'Operator reason',
      detailValue: reason,
      detailPlaceholder: 'What was corrected before replay',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkReplayConfirmState(ids, value)),
      onConfirm: () => runBulkReplay(ids, reason),
    };
  }

  function buildBulkArchiveConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    return {
      title: 'Bulk archive exchanges',
      message: `Archive ${count} selected exchange${count === 1 ? '' : 's'} out of the recovery queue. This does not retry or replay them -- it only records that no further action is planned. Archived exchanges stay visible under the "Archived" status filter.`,
      confirmLabel: `Archive ${count} exchange${count === 1 ? '' : 's'}`,
      risk: 'medium',
      detailLabel: 'Operator reason',
      detailValue: reason,
      detailPlaceholder: 'Why no further action is planned',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkArchiveConfirmState(ids, value)),
      onConfirm: () => runBulkArchive(ids, reason),
    };
  }

  function renderExchangesTable(rows: ExchangeSummary[]) {
    const ids = rows.map((exchange) => exchange.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    return (
      <table className={`data-table data-table-${density}${recoveryMode ? ' recovery-table' : ''}`}>
        <thead>
          <tr>
            <th>
              <input type="checkbox" aria-label="Select all in this table" checked={allSelected} onChange={() => toggleSelectAll(ids)} />
            </th>
            <th>Exchange</th>
            {recoveryMode ? <th>Partner</th> : <th>Kind</th>}
            {visibleColumns.document ? <th>Document</th> : null}
            <th>Status</th>
            {visibleColumns.correlation ? <th>Correlation</th> : null}
            {visibleColumns.updated ? <th>Updated</th> : null}
            {recoveryMode ? <th>Recovery actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((exchange) => (
            <tr key={exchange.id} className={selectedIds.has(exchange.id) ? 'selected-row' : undefined}>
              <td data-label="Select">
                <input
                  type="checkbox"
                  aria-label={`Select exchange ${exchange.id}`}
                  checked={selectedIds.has(exchange.id)}
                  onChange={() => toggleSelected(exchange.id)}
                />
              </td>
              <td data-label="Exchange">
                <div className="table-primary">
                  <Link to={{ pathname: `/operations/exchanges/${exchange.id}`, search }}>
                    {formatExchangeTitle(exchange.exchangeKind, exchange.documentType, exchange.referenceId)}
                  </Link>
                  {visibleColumns.technicalIds ? (
                    <span className="inline-copy">
                      <span className="mono" title={exchange.id}>{shortId(exchange.id)}</span>
                      <CopyButton value={exchange.id} label="Copy exchange ID" />
                    </span>
                  ) : null}
                </div>
              </td>
              {recoveryMode ? <td data-label="Partner">{exchange.partnerId ?? 'Not assigned'}</td> : <td data-label="Kind">{formatExchangeKind(exchange.exchangeKind)}</td>}
              {visibleColumns.document ? <td data-label="Document">
                <div className="table-primary">
                  <span>{exchange.documentType} document</span>
                  {visibleColumns.technicalIds ? (
                    <span className="inline-copy">
                      <span className="mono" title={exchange.documentId ?? undefined}>{exchange.documentId ? shortId(exchange.documentId) : 'n/a'}</span>
                      <CopyButton value={exchange.documentId} label="Copy document ID" />
                      {exchange.documentId ? <RelatedLinks links={[documentDetailLink(exchange.documentId, search)]} /> : null}
                    </span>
                  ) : null}
                </div>
              </td> : null}
              <td data-label="Status"><StatusBadge value={exchange.status} /></td>
              {visibleColumns.correlation ? <td data-label="Correlation">
                <div className="table-primary">
                  <span className="inline-copy" title={exchange.correlationId ?? undefined}>
                    <span>{formatCorrelation(exchange.correlationId)}</span>
                    <CopyButton value={exchange.correlationId} label="Copy correlation ID" />
                    {exchange.correlationId ? (
                      <RelatedLinks links={[
                        documentsByCorrelationLink(exchange.correlationId, search),
                        executionsByCorrelationLink(exchange.correlationId, search),
                      ]}
                      />
                    ) : null}
                  </span>
                  {visibleColumns.technicalIds ? (
                    <span className="inline-copy">
                      <span className="mono">{exchange.traceId ?? 'no-trace'}</span>
                      <CopyButton value={exchange.traceId} label="Copy trace ID" />
                      {exchange.traceId ? <RelatedLinks links={[documentsByTraceLink(exchange.traceId, search)]} /> : null}
                    </span>
                  ) : null}
                </div>
              </td> : null}
              {visibleColumns.updated ? <td data-label="Updated" title={new Date(exchange.updatedAt).toLocaleString('en-GB')}>{formatRecoveryAge(exchange.updatedAt)}</td> : null}
              {recoveryMode ? <td data-label="Recovery actions" className="actions-cell recovery-actions">
                {canRequestRetry && canRetryExchange(exchange) ? <button type="button" onClick={() => setConfirm(buildBulkRetryConfirmState([exchange.id], ''))}>Retry</button> : null}
                {canRequestReplay && canReplayExchange(exchange) ? <button type="button" onClick={() => setConfirm(buildBulkReplayConfirmState([exchange.id], ''))}>Replay</button> : null}
                {canDeadLetter && canDeadLetterExchange(exchange) ? <button type="button" className="danger-link" onClick={() => setConfirm(buildBulkDeadLetterConfirmState([exchange.id], ''))}>Quarantine</button> : null}
                {canArchive && canArchiveExchange(exchange) ? <button type="button" className="ghost-button" onClick={() => setConfirm(buildBulkArchiveConfirmState([exchange.id], ''))}>Archive</button> : null}
              </td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="page-grid">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h1>{recoveryMode ? 'Recovery Queue' : 'Exchange runtime'}</h1>
            <p>{recoveryMode ? 'Triage unresolved failed, waiting-retry, and dead-lettered exchanges, then apply a controlled recovery action.' : 'Inspect the current runtime ledger for exchange execution, failed work, retry backlog, and dead-letter state.'}</p>
          </div>
          <div className="panel-actions">
            <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
            <button type="button" onClick={() => void refresh()}>
              Refresh
            </button>
            {recoveryMode ? <Link className="button-link" to={{ pathname: '/operations/exchanges', search }}>View resolved history</Link> : null}
          </div>
        </div>
        <div className="operations-card-grid">
          <article className="operations-card">
            <span className="operations-card-label">Visible failed</span>
            <strong>{failedCount}</strong>
            <p>Exchanges in the current result set that still need operator review.</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Waiting retry</span>
            <strong>{waitingRetryCount}</strong>
            <p>Retry requests that have been accepted and are waiting on runtime processing.</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Dead-lettered</span>
            <strong>{deadLetterCount}</strong>
            <p>Dead-letter entries currently visible through the active filters.</p>
          </article>
        </div>
        <div className="status-chip-row" aria-label="Exchange status focus">
          {statusFocusOptions.map((option) => (
            <button
              key={option.label}
              type="button"
              className={`status-chip${filters.status === option.value ? ' active' : ''}`}
              onClick={() => applyFilters({ ...filters, page: 1, status: option.value })}
            >
              {recoveryMode && !option.value ? 'All unresolved' : option.label}
            </button>
          ))}
        </div>
        <div className="toolbar toolbar-grid">
          <select
            aria-label="Exchange kind"
            value={filters.exchangeKind ?? ''}
            onChange={(event) => applyFilters({ ...filters, page: 1, exchangeKind: event.target.value as ExchangeKind | '' })}
          >
            <option value="">All exchange kinds</option>
            {exchangeKinds.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
          </select>
          <input
            aria-label="Document type"
            placeholder="Document type"
            value={filters.documentType ?? ''}
            onChange={(event) => applyFilters({ ...filters, page: 1, documentType: event.target.value })}
          />
          <input
            aria-label="Correlation ID"
            placeholder="Correlation ID"
            value={filters.correlationId ?? ''}
            onChange={(event) => applyFilters({ ...filters, page: 1, correlationId: event.target.value })}
          />
          <input
            aria-label="Reference ID"
            placeholder="Reference ID"
            value={filters.referenceId ?? ''}
            onChange={(event) => applyFilters({ ...filters, page: 1, referenceId: event.target.value })}
          />
          <input
            aria-label="Trace ID"
            placeholder="Trace ID"
            value={filters.traceId ?? ''}
            onChange={(event) => applyFilters({ ...filters, page: 1, traceId: event.target.value })}
          />
          <button
            type="button"
            className="ghost-button"
            onClick={() =>
              applyFilters({
                page: 1,
                pageSize: 20,
                status: '',
                exchangeKind: '',
                documentType: '',
                correlationId: '',
                traceId: '',
                referenceId: '',
              })
            }
          >
            Reset filters
          </button>
        </div>
        <div className="table-controls" aria-label="Exchange table display options">
          <div className="segmented-control" aria-label="Table density">
            <button type="button" aria-pressed={density === 'cozy'} className={density === 'cozy' ? 'active' : ''} onClick={() => setDensity('cozy')}>Cozy</button>
            <button type="button" aria-pressed={density === 'compact'} className={density === 'compact' ? 'active' : ''} onClick={() => setDensity('compact')}>Compact</button>
          </div>
          <label className="inline-check"><input type="checkbox" checked={visibleColumns.technicalIds} onChange={(event) => setVisibleColumns((current) => ({ ...current, technicalIds: event.target.checked }))} /> Technical IDs</label>
          <label className="inline-check"><input type="checkbox" checked={visibleColumns.document} onChange={(event) => setVisibleColumns((current) => ({ ...current, document: event.target.checked }))} /> Document</label>
          <label className="inline-check"><input type="checkbox" checked={visibleColumns.correlation} onChange={(event) => setVisibleColumns((current) => ({ ...current, correlation: event.target.checked }))} /> Correlation</label>
          <label className="inline-check"><input type="checkbox" checked={visibleColumns.updated} onChange={(event) => setVisibleColumns((current) => ({ ...current, updated: event.target.checked }))} /> Updated</label>
        </div>
        {actionError ? <ErrorState message={actionError} /> : null}
        {isLoading ? <LoadingState label={recoveryMode ? 'Loading recovery queue...' : 'Loading exchange runtime...'} /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && exchanges.length === 0 ? <EmptyState label={recoveryMode ? 'No unresolved exchanges matched the current filters.' : 'No exchanges matched the current runtime filters.'} /> : null}
        {!isLoading && !error && exchanges.length > 0 ? (
          <>
            <div className="table-controls" aria-label="Exchange grouping and bulk actions">
              <div className="segmented-control" aria-label="Group by">
                <button type="button" aria-pressed={groupBy === 'none'} className={groupBy === 'none' ? 'active' : ''} onClick={() => setGroupBy('none')}>No grouping</button>
                <button type="button" aria-pressed={groupBy === 'partner'} className={groupBy === 'partner' ? 'active' : ''} onClick={() => setGroupBy('partner')}>By partner</button>
              </div>
              <span>{selectedIds.size} of {exchanges.length} visible selected</span>
              {(canRequestRetry || canDeadLetter || canRequestReplay || canArchive) ? (
                <div className="panel-actions">
                  {canRequestRetry ? (
                    <button type="button" disabled={eligibleRetryIds.length === 0} onClick={() => setConfirm(buildBulkRetryConfirmState(eligibleRetryIds, ''))}>
                      Bulk retry ({eligibleRetryIds.length})
                    </button>
                  ) : null}
                  {canDeadLetter ? (
                    <button type="button" className="danger-link" disabled={eligibleDeadLetterIds.length === 0} onClick={() => setConfirm(buildBulkDeadLetterConfirmState(eligibleDeadLetterIds, ''))}>
                      Bulk dead-letter ({eligibleDeadLetterIds.length})
                    </button>
                  ) : null}
                  {canRequestReplay ? (
                    <button type="button" disabled={eligibleReplayIds.length === 0} onClick={() => setConfirm(buildBulkReplayConfirmState(eligibleReplayIds, ''))}>
                      Bulk replay ({eligibleReplayIds.length})
                    </button>
                  ) : null}
                  {canArchive ? (
                    <button type="button" className="ghost-button" disabled={eligibleArchiveIds.length === 0} onClick={() => setConfirm(buildBulkArchiveConfirmState(eligibleArchiveIds, ''))}>
                      Bulk archive ({eligibleArchiveIds.length})
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {groupBy === 'none' ? renderExchangesTable(exchanges) : (
              <div className="page-stack">
                {groups.map((group) => (
                  <details key={group.key} className="panel" open>
                    <summary>
                      <strong>{group.label}</strong>
                      {' — '}{group.exchanges.length} exchange{group.exchanges.length === 1 ? '' : 's'}
                    </summary>
                    {renderExchangesTable(group.exchanges)}
                  </details>
                ))}
              </div>
            )}
            {!recoveryMode ? <div className="pagination-bar">
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, (current.page ?? 1) - 1) }))}
                disabled={(filters.page ?? 1) <= 1}
              >
                Previous
              </button>
              <span>Page {filters.page ?? 1}</span>
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}
                disabled={(data?.page ?? 1) * (data?.pageSize ?? 20) >= (data?.total ?? 0)}
              >
                Next
              </button>
            </div> : null}
          </>
        ) : null}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function formatExchangeKind(value: ExchangeKind): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character: string) => character.toUpperCase());
}

function formatExchangeTitle(kind: ExchangeKind, documentType: string, referenceId?: string): string {
  const action = formatExchangeKind(kind).replace(/^Document /, '');
  return referenceId ? `${action} ${documentType} - Ref ${referenceId}` : `${action} ${documentType}`;
}

function shortId(value: string): string {
  const normalized = value.replace(/^(doc|exg|flowexec|flow|partner)_/, '$1 ');
  return normalized.length > 18 ? `${normalized.slice(0, 18)}...` : normalized;
}

function formatCorrelation(value?: string): string {
  if (!value) {
    return 'n/a';
  }

  const [eventType, ...rest] = value.split('-');
  const suffix = rest[rest.length - 1];
  return suffix && /^\d+$/.test(suffix) ? `${formatReadableLabel(eventType)} - ${suffix}` : value;
}

function formatReadableLabel(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character: string) => character.toUpperCase());
}

function formatRecoveryAge(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
